export const API_BASE = import.meta.env.VITE_API_BASE
export const BOOK_MANAGEMENT_API_BASE = import.meta.env.VITE_BOOK_MANAGEMENT_API_BASE || 'https://azr-books-func-02-r1.azurewebsites.net'

export interface Book {
  slug: string
  title: string
  authors?: string[]
  year?: number
  license?: string
  type: 'book' | 'paper'
  coverUrl?: string
  fileSize?: string
  fileType?: string
  description?: string
}

export async function requestDownload(input: { email:string; itemId:string; captchaToken:string }){
  try {
    const res = await fetch(`${API_BASE}/api/request-download`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(input),
      mode: 'cors' // Explicitly set CORS mode
    })
    if(!res.ok){
      const t = await res.text().catch(()=> '')
      throw new Error(t || `HTTP ${res.status}: ${res.statusText}`)
    }
    return true
  } catch (error) {
    // Handle network errors more gracefully
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server. Check CORS configuration.')
    }
    throw error
  }
}

export async function searchBooks(query: string, bookType: string = '', page: number = 1, perPage: number = 50): Promise<{books: Book[], pagination: any}> {
  try {
    const params = new URLSearchParams({
      search: query,
      page: page.toString(),
      per_page: perPage.toString()
    })
    
    if (bookType) {
      params.append('type', bookType)
    }

    const url = `${BOOK_MANAGEMENT_API_BASE}/api/get-books?${params}`
    console.log('Fetching from URL:', url)
    console.log('BOOK_MANAGEMENT_API_BASE:', BOOK_MANAGEMENT_API_BASE)

    const res = await fetch(url, {
      method: 'GET',
      headers: { 
        'Accept': 'application/json'
      },
      mode: 'cors'
    })
    
    if (!res.ok) {
      const errorText = await res.text().catch(() => '')
      throw new Error(errorText || `HTTP ${res.status}: ${res.statusText}`)
    }
    
    const data = await res.json()
    return {
      books: data.books || [],
      pagination: data.pagination || {}
    }
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server. Check CORS configuration.')
    }
    throw error
  }
}

export interface PaginationInfo {
  page: number
  per_page: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface PaginatedBooksResponse {
  books: Book[]
  total_count: number
  page: number
  per_page: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export async function getAllBooks(
  page: number = 1, 
  perPage: number = 12,
  search: string = '',
  type: string = ''
): Promise<PaginatedBooksResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    })

    if (search.trim()) {
      params.append('search', search.trim())
    }

    if (type.trim()) {
      params.append('type', type.trim())
    }

    const url = `${BOOK_MANAGEMENT_API_BASE}/api/get_all_books?${params}`
    console.log('Fetching all books from URL:', url)

    const res = await fetch(url, {
      method: 'GET',
      headers: { 
        'Accept': 'application/json'
      },
      mode: 'cors'
    })
    
    if (!res.ok) {
      const errorText = await res.text().catch(() => '')
      throw new Error(errorText || `HTTP ${res.status}: ${res.statusText}`)
    }
    
    const response = await res.json()
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch books')
    }

    return response.data
  } catch (error) {
    console.error('Failed to fetch all books:', error)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server. Check CORS configuration.')
    }
    throw error
  }
}

export async function getFeaturedBooks(count: number = 12): Promise<Book[]> {
  try {
    const params = new URLSearchParams({
      count: count.toString()
    })

    const url = `${BOOK_MANAGEMENT_API_BASE}/api/get_featured_books?${params}`
    console.log('Fetching featured books from URL:', url)

    const res = await fetch(url, {
      method: 'GET',
      headers: { 
        'Accept': 'application/json'
      },
      mode: 'cors'
    })
    
    if (!res.ok) {
      const errorText = await res.text().catch(() => '')
      throw new Error(errorText || `HTTP ${res.status}: ${res.statusText}`)
    }
    
    const data = await res.json()
    return data.books || []
  } catch (error) {
    console.error('Failed to fetch featured books:', error)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server. Check CORS configuration.')
    }
    throw error
  }
}